import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  Pressable,
  FlatList,
  Modal,
  ActivityIndicator,
} from "react-native";
import { AddCircle, CloseCircle, SearchNormal1 } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";

import MemberRow from "../add-members/member-row";
import ScSearchInput from "../../ui-kit/search-bar";
import ScButton from "../../ui-kit/button";
import ScDropdown from "../../ui-kit/dropdown-select";
import { useAuth } from "@/src/contexts/auth.context";
import { useGetUsers } from "@/src/services/api/groups/groups.hooks";
import { useUserIdsByCountry } from "@/src/services/profile/profile.hooks";
import { useUsersByIdsQuery } from "@/src/services/users.blocks.api";
import { COUNTRIES, getCountryFlag } from "@/src/constants/countries";
import { BlocksUser, InvitableMember } from "@/src/models/user.model";
import { useTheme } from "@/src/styles/theme-provider";
import { formatUserName } from "@/src/utils/string.utils";

type InviteMembersModalProps = Readonly<{
  members?: InvitableMember[];
  visible: boolean;
  onClose: () => void;
  onInvite: (members: InvitableMember[]) => void;
  existingMemberIds?: string[];
}>;

const toInvitable = (user: BlocksUser): InvitableMember => ({
  itemId: user.itemId,
  userName: user.email,
  firstName: user.firstName ?? "",
  lastName: user.lastName ?? "",
  displayName: formatUserName(user.firstName, user.lastName) || user.email,
  profileImageUrl: user.profileImageUrl ?? undefined,
  email: user.email,
});

const InviteMembersModal = ({
  members = [],
  visible,
  onClose,
  onInvite,
  existingMemberIds,
}: InviteMembersModalProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();

  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedMembers, setSelectedMembers] =
    useState<InvitableMember[]>(members);

  // Reset when modal opens/closes
  useEffect(() => {
    if (visible) {
      setSelectedMembers(members);
      setSearch("");
      setSelectedCountry("");
    }
  }, [visible, members]);

  const selectedIds = new Set(selectedMembers.map((member) => member.itemId));

  const toggle = (member: InvitableMember) => {
    setSelectedMembers((prev) =>
      prev.some((addedMember) => addedMember.itemId === member.itemId)
        ? prev.filter((addedMember) => addedMember.itemId !== member.itemId)
        : [...prev, member]
    );
  };

  const handleInvite = () => {
    onInvite?.(selectedMembers);
    onClose();
  };

  const countryOptions = useMemo(
    () => [
      { label: t("LABEL_ALL_COUNTRIES"), value: "" },
      ...COUNTRIES.map((country) => ({
        label: `${getCountryFlag(country.name)}  ${country.name}`.trim(),
        value: country.name,
      })),
    ],
    [t],
  );

  // Asks the gateway for one country instead of downloading every profile and
  // matching in memory, so nothing is fetched until a country is picked and there
  // is no 1000-profile ceiling.
  const { data: countryIds, isFetching: isResolvingCountryIds } =
    useUserIdsByCountry(selectedCountry);
  const countryUserIds = countryIds ?? [];

  const { user: currentUser } = useAuth();
  const { data, fetchNextPage, isFetchingNextPage } = useGetUsers(search);
  const searchUsers: InvitableMember[] =
    data?.pages.flatMap((page) => page.users) ?? [];

  const { data: byIdsData, isFetching: isFetchingCountryUsers } =
    useUsersByIdsQuery(countryUserIds);

  // Picking a country is two sequential requests — resolve the ids from the
  // profiles, then look those users up in IAM, in batches. Without this the list
  // fell through to "no users from X are available to invite" while it was still
  // fetching, which reads as an answer rather than a wait. The third clause covers
  // the gap between the ids arriving and the user lookup starting, where both
  // queries are briefly idle.
  const isCountryLoading =
    !!selectedCountry &&
    (isResolvingCountryIds ||
      isFetchingCountryUsers ||
      (countryUserIds.length > 0 && byIdsData === undefined));
  const countryUsers: InvitableMember[] = (byIdsData?.users ?? []).map(
    toInvitable,
  );

  const baseList = selectedCountry ? countryUsers : searchUsers;

  const filtered = baseList.filter((member) => {
    // `||`, not `??`: `??` only falls back when displayName is null/undefined, so
    // a member who HAS a display name that does not match returned false and the
    // username was never checked — nobody with a display name could be found by
    // username.
    const term = search.toLowerCase();
    const matchesSearch =
      !search ||
      (member.displayName?.toLowerCase().includes(term) ?? false) ||
      member.userName.toLowerCase().includes(term);

    // Never offer to invite the person doing the inviting — they are already in
    // the group (creator) or are the one adding members.
    return (
      matchesSearch &&
      member.itemId !== currentUser?.itemId &&
      !existingMemberIds?.includes(member.itemId)
    );
  });

  const totalCount = selectedCountry
    ? filtered.length
    : (data?.pages[0]?.count ?? 0);

  const allSelected =
    filtered.length > 0 &&
    filtered.every((member) => selectedIds.has(member.itemId));

  const selectAll = () => {
    setSelectedMembers((prev) => {
      const map = new Map(prev.map((member) => [member.itemId, member]));
      filtered.forEach((member) => map.set(member.itemId, member));
      return [...map.values()];
    });
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={themeVars as any} className="flex-1">
        <View
          className="flex-1 items-center justify-center px-4"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <View
            className="w-full max-w-md bg-chalk rounded-2xl"
            style={{ height: "80%", maxHeight: 600 }}
          >
            {/* Header */}
            <View className="flex-row justify-between items-center gap-3 px-5 pt-5 pb-4 border-b border-ash-3">
              <Text className="text-base text-onyx">
                {t("LABEL_INVITE_USER")}
              </Text>
              <Pressable onPress={onClose} className="p-1.5 rounded-full hover:bg-ash-3">
                <CloseCircle size={20} color={"var(--color-ash-1)"} />
              </Pressable>
            </View>

            {/* Search + country filter */}
            <View className="flex-row items-center gap-2 px-5 pt-4 pb-2">
              <View className="flex-1">
                <ScSearchInput
                  value={search}
                  placeholderKey="LABEL_SEARCH_USERS"
                  onChangeText={setSearch}
                />
              </View>
              <ScDropdown
                options={countryOptions}
                value={selectedCountry}
                onChange={setSelectedCountry}
                placeholder={t("LABEL_FILTER_BY_COUNTRY")}
                triggerClassName="py-3 bg-chalk w-36"
                menuWidth={240}
                searchable
                searchPlaceholder={t("LABEL_SEARCH_COUNTRY")}
                noResultsLabel={t("LABEL_NO_COUNTRY_MATCH")}
              />
            </View>

            {/* Count + actions */}
            <View className="flex-row justify-between items-center px-5 py-2">
              <Text className="text-sm text-slate">
                {selectedIds.size > 0
                  ? t("LABEL_SELECTED_COUNT", { count: selectedIds.size })
                  : t("LABEL_PEOPLE_COUNT", { count: totalCount })}
              </Text>
              <View className="flex-row items-center gap-1">
                <Pressable
                  onPress={selectAll}
                  disabled={filtered.length === 0 || allSelected}
                  className="px-2 py-1 rounded-lg hover:bg-ash-3"
                >
                  <Text
                    className={
                      filtered.length === 0 || allSelected
                        ? "text-ash-2"
                        : "text-primary"
                    }
                  >
                    {t("BTN_SELECT_ALL")}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => setSelectedMembers([])}
                  className="px-2 py-1 rounded-lg hover:bg-ash-3"
                >
                  <Text className="text-slate">{t("BTN_CLEAR")}</Text>
                </Pressable>
              </View>
            </View>

            {/* List */}
            <FlatList
              data={filtered}
              keyExtractor={(item) => item.itemId}
              renderItem={({ item }) => (
                <View className="px-5">
                  <MemberRow
                    member={item}
                    selected={selectedIds.has(item.itemId)}
                    onToggle={() => toggle(item)}
                  />
                </View>
              )}
              showsVerticalScrollIndicator={false}
              ItemSeparatorComponent={() => <View className="mx-5 border-b border-ash-1" />}
              // Three ways to end up with nobody, and they need different wording:
              // a country with no members, a search that matched nothing, and
              // everyone already being in the group.
              ListEmptyComponent={
                isCountryLoading ? (
                  <View className="items-center justify-center gap-2 px-6 py-10">
                    <ActivityIndicator
                      size="small"
                      color={themeVars["--color-primary-default"] as string}
                    />
                    <Text className="text-sm text-slate text-center">
                      {t("LABEL_LOADING")}
                    </Text>
                  </View>
                ) : (
                <View className="items-center justify-center gap-2 px-6 py-10">
                  <SearchNormal1
                    size={32}
                    color={themeVars["--color-slate-default"] as string}
                  />
                  <Text className="text-base font-semibold text-onyx text-center">
                    {t("LABEL_NO_USERS_FOUND")}
                  </Text>
                  <Text className="text-sm text-slate text-center">
                    {selectedCountry
                      ? t("LABEL_NO_USERS_IN_COUNTRY", {
                          country: selectedCountry,
                        })
                      : search
                        ? t("LABEL_NO_USERS_MATCH_SEARCH")
                        : t("LABEL_NO_USERS_TO_INVITE")}
                  </Text>
                </View>
                )
              }
              onEndReached={() => {
                if (!selectedCountry) fetchNextPage();
              }}
              onEndReachedThreshold={0.5}
              contentContainerStyle={{ paddingBottom: 8 }}
              className="flex-1"
              ListFooterComponent={
                isFetchingNextPage && !selectedCountry ? (
                  <Text className="text-center py-4 text-slate">Loading more...</Text>
                ) : null
              }
            />

            {/* Footer */}
            <View className="flex-row items-center justify-end gap-3 px-5 py-4 border-t border-ash-3">
              <ScButton text="BTN_CANCEL" mode="outlined" onPress={onClose} />
              <ScButton
                text="BTN_INVITE"
                onPress={handleInvite}
                icon={<AddCircle size={18} color="white" />}
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default InviteMembersModal;
